import { AdminGuard } from "@appretio/shared";
import { ErrorBoundary, semanticThemeTokens } from "@appretio/ui-kit";
import { lazy, type ReactNode, Suspense } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import { PageFrame } from "./shared/components/pageFrame/PageFrame";
import {
  SideNavLayout,
  type SideNavPage
} from "./shared/components/sideNavLayout/SideNavLayout";

const AccessPage = lazy(() =>
  import("./settings/personal/accessPage/AccessPage").then((m) => ({
    default: m.AccessPage
  }))
);

const GeneralPage = lazy(() =>
  import("./settings/organisation/generalPage/GeneralPage").then((m) => ({
    default: m.GeneralPage
  }))
);

const MembersPage = lazy(() =>
  import("./settings/organisation/membersPage/MembersPage").then((m) => ({
    default: m.MembersPage
  }))
);

const ViewRolesPage = lazy(() =>
  import("./settings/organisation/viewRolesPage/ViewRolesPage").then((m) => ({
    default: m.ViewRolesPage
  }))
);

const InSeasonPage = lazy(() =>
  import("./settings/modules/inSeasonPage/InSeasonPage").then((m) => ({
    default: m.InSeasonPage
  }))
);

const ScrapersPage = lazy(() =>
  import("./operations/scrapersPage/ScrapersPage").then((m) => ({
    default: m.ScrapersPage
  }))
);

const IntegrationRunsPage = lazy(() =>
  import("./operations/integrationRunsPage/IntegrationRunsPage").then((m) => ({
    default: m.IntegrationRunsPage
  }))
);

const background = semanticThemeTokens.backgroundGradient[1];

/** A side-menu area: the layout, its index redirect, and a route per page. */
function sideNavRoute(
  path: string,
  title: string,
  pages: (SideNavPage & { page: ReactNode })[]
): RouteObject {
  return {
    path,
    element: <SideNavLayout title={title} pages={pages} />,
    handle: { background },
    children: [
      { index: true, element: <Navigate to={pages[0].path} replace /> },
      ...pages.map(({ path: pagePath, handle, page }) => {
        // The page's own loading state, so the title and spinner stay put.
        const loadingFrame = (
          <PageFrame
            title={handle.label}
            description={"\u00a0"}
            loadState="loading"
          />
        );
        const element = (
          <ErrorBoundary level="route">
            <Suspense fallback={loadingFrame}>{page}</Suspense>
          </ErrorBoundary>
        );
        // AppLayout reads the background from the last two matches; under
        // the guard, those are this route and its index child.
        return handle.requires === "admin"
          ? {
              path: pagePath,
              handle: { ...handle, background },
              element: <AdminGuard fallback={loadingFrame} />,
              children: [{ index: true, element }]
            }
          : { path: pagePath, handle: { ...handle, background }, element };
      })
    ]
  };
}

export const settingsRoute = sideNavRoute("settings", "Settings", [
  {
    path: "access",
    // Everyone: a user holding no roles is exactly who needs to read what
    // they hold.
    handle: { label: "Access", group: "Personal" },
    page: <AccessPage />
  },
  {
    path: "general",
    handle: { label: "General", group: "Organisation" },
    page: <GeneralPage />
  },
  {
    path: "members",
    handle: {
      label: "Members and roles",
      group: "Organisation",
      requires: "admin"
    },
    page: <MembersPage />
  },
  {
    path: "view-roles",
    handle: { label: "View roles", group: "Organisation", requires: "admin" },
    page: <ViewRolesPage />
  },
  {
    path: "in-season",
    handle: { label: "In-season", group: "Modules" },
    page: <InSeasonPage />
  }
]);

export const operationsRoute = sideNavRoute("operations", "Operations", [
  {
    path: "integration-runs",
    handle: { label: "Integration runs", requires: "admin" },
    page: <IntegrationRunsPage />
  },
  // The API mounts the scraper admin routes only outside prod.
  ...(import.meta.env.VITE_ENVIRONMENT === "production"
    ? []
    : [
        {
          path: "scrapers",
          handle: {
            label: "Scrapers",
            requires: "admin"
          } satisfies SideNavPage["handle"],
          page: <ScrapersPage />
        }
      ])
]);
